import React, { useMemo, useRef, useState } from "react";

/**
 * MultiInput (Controlled or Uncontrolled)
 * - Enter / comma commits one email
 * - Paste multiple emails (comma/space/semicolon separated) commits all at once
 * - Backspace on empty removes last chip
 *
 * Props:
 *  value: string[] (for controlled mode)
 *  onChange: (emails: string[]) => void
 *  placeholder: string
 *  label: string
 *  regex: RegExp (email validation)
 */
export default function MultiInput({
  value,
  onChange,
  placeholder,
  label,
  regex,
}) {
  const isControlled = Array.isArray(value);
  const [internal, setInternal] = useState([]);

  const emails = isControlled ? value : internal;

  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const lowerSet = useMemo(() => {
    return new Set((emails || []).map((e) => String(e).toLowerCase()));
  }, [emails]);

  const setEmails = (next) => {
    onChange?.(next);
    if (!isControlled) setInternal(next);
  };

  // Commit a single email (used by Enter/comma)
  const commitOne = (raw) => {
    const email = (raw || "").trim();
    if (!email) return;

    if (!regex?.test(email)) {
      setError("Invalid email address");
      return;
    }

    const key = email.toLowerCase();
    if (lowerSet.has(key)) {
      setError("Email already added");
      return;
    }

    setError("");
    setEmails([...(emails || []), email]);
    setInput("");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const removeEmail = (index) => {
    const next = (emails || []).filter((_, i) => i !== index);
    setEmails(next);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (e) => {
    // Enter or comma commits current input
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      // remove trailing commas/spaces
      commitOne(input.replace(/[,\s]+$/, ""));
      return;
    }

    // Backspace on empty removes last chip
    if (
      e.key === "Backspace" &&
      input.trim() === "" &&
      (emails || []).length > 0
    ) {
      removeEmail((emails || []).length - 1);
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text");

    const parts = text
      .split(/[\s,;]+/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (parts.length <= 1) return; // let normal paste happen for single value

    e.preventDefault();

    const next = [...(emails || [])];
    const nextLower = new Set(next.map((x) => String(x).toLowerCase()));

    let hadInvalid = false;
    let firstInvalid = "";

    for (const raw of parts) {
      const email = raw.trim();
      if (!email) continue;

      if (!regex?.test(email)) {
        hadInvalid = true;
        if (!firstInvalid) firstInvalid = email;
        continue; // skip invalid
      }

      const key = email.toLowerCase();
      if (nextLower.has(key)) continue; // skip duplicates

      next.push(email);
      nextLower.add(key);
    }

    if (hadInvalid) {
      setError(`Some emails were invalid (e.g., ${firstInvalid})`);
    } else {
      setError("");
    }

    setEmails(next);
    setInput("");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div style={styles.field}>
      {label ? <label style={styles.label}>{label}</label> : null}

      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          ...styles.inputBox,
          borderColor: error ? "#dc3545" : "#d9d9d9",
        }}
      >
        {(emails || []).map((email, idx) => (
          <span key={`${email}-${idx}`} style={styles.chip}>
            <span style={styles.chipText}>{email}</span>
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                removeEmail(idx);
              }}
              style={styles.chipRemove}
              aria-label={`Remove ${email}`}
              title="Remove"
            >
              ×
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => setError("")}
          onPaste={handlePaste}
          placeholder={(emails || []).length === 0 ? placeholder : ""}
          style={styles.input}
        />
      </div>

      {error ? <div style={styles.error}>{error}</div> : null}
    </div>
  );
}

const styles = {
  field: {
    width: "100%",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  },
  label: {
    display: "block",
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 600,
    color: "black",
  },
  inputBox: {
    width: "100%",
    minHeight: 38,
    padding: "6px 10px",
    border: "1px solid #d9d9d9",
    borderRadius: 3,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    boxSizing: "border-box",
  },
  input: {
    border: "none",
    outline: "none",
    flex: 1,
    minWidth: 160,
    height: 24,
    fontSize: 14,
    padding: 0,
    margin: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 8px",
    borderRadius: 12,
    background: "#f5f5f5",
    border: "1px solid #e5e5e5",
    fontSize: 13,
    maxWidth: "100%",
  },
  chipText: {
    color: "#333",
    maxWidth: 260,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chipRemove: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    lineHeight: 1,
    fontSize: 14,
    color: "#666",
    padding: 0,
  },
  error: {
    marginTop: 6,
    fontSize: 12,
    color: "#dc3545",
  },
};
